import {
  ListObjectsV2Command,
  DeleteObjectCommand,
  GetObjectCommand,
  S3Client,
} from "@aws-sdk/client-s3";
import {getSignedUrl} from "@aws-sdk/s3-request-presigner";
import {Upload} from "@aws-sdk/lib-storage";
import React, {useEffect, useState, useRef, useCallback} from "react";
import {
  Table,
  Button,
  Progress,
  Dropdown,
  Menu,
  Input,
  App
} from "antd";
import {useDropzone} from "react-dropzone";
import {
  UploadOutlined,
  MoreOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  FileOutlined,
  KeyOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import {getS3ClientWithCredentials} from "../utils/getS3Client";
import {getOrCreateShareAndCredentials, getCredentialsForShare} from "../utils/getCredentials";
import "../styles/index.scss";

interface FileItem {
  key: string;
  name: string;
  date: string;
  owner: string;
  size: string;
  sizeInBytes: number;
  type: string;
  s3Key: string;
}

interface FileBrowserProps {
  accessToken?: string;
}

export default function FileBrowser({accessToken}: FileBrowserProps = {}) {
  const {message: messageApi} = App.useApp();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [client, setClient] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gettingCredentials, setGettingCredentials] = useState(false);
  const [userId, setUserId] = useState<string>('');
  const [currentShareId, setCurrentShareId] = useState<string>('');
  const [bucketName, setBucketName] = useState<string>("");
  const [bucketMaxSize, setBucketMaxSize] = useState<number>(10 * 1024 * 1024 * 1024);
  const [usedStorage, setUsedStorage] = useState<number>(0);
  const [hasMaxSize, setHasMaxSize] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [uploadingFiles, setUploadingFiles] = useState<Array<{
    id: string;
    name: string;
    progress: number;
    status: 'uploading' | 'success' | 'error';
    speed?: number;
    timeRemaining?: number;
  }>>([]);
  const [downloadingFiles, setDownloadingFiles] = useState<Map<string, {
    progress: number;
    status: 'downloading' | 'success' | 'error';
  }>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);

  const storagePercentage = hasMaxSize ? (usedStorage / bucketMaxSize) * 100 : 0;

  const isDirectTokenMode = !!accessToken;

  const formatSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    } else if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${bytes} B`;
    }
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    return `${formatSize(bytesPerSecond)}/s`;
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const formatExpirationDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const loadFiles = useCallback(async (s3Client: any, bucket?: string) => {
    const bucketToUse = bucket || bucketName;

    if (!bucketToUse) {
      throw new Error('Token not found');
    }

    const result = await s3Client.send(
      new ListObjectsV2Command({Bucket: bucketToUse})
    );

    const transformedFiles: FileItem[] = (result.Contents || []).map((file: any, index: number) => ({
      key: index.toString(),
      name: file.Key || 'Unknown',
      date: file.LastModified ? new Date(file.LastModified).toLocaleDateString('ru-RU') : '-',
      // owner: 'Service Delivery Part...',
      size: file.Size ? `${(file.Size / (1024 * 1024)).toFixed(2)} MB` : '0 MB',
      sizeInBytes: file.Size || 0,
      type: getFileTypeFromName(file.Key || ''),
      s3Key: file.Key || ''
    }));

    const totalSize = transformedFiles.reduce((sum, file) => sum + file.sizeInBytes, 0);
    setUsedStorage(totalSize);

    setFiles(transformedFiles);
  }, [bucketName])

  const handleGetCredentialsForToken = useCallback(async () => {
    if (!accessToken) return;

    setGettingCredentials(true);
    setLoading(true);
    setErrorMessage('');
    try {
      const credentials = await getCredentialsForShare(accessToken);

      if (!credentials.bucket) {
        throw new Error('Token not found');
      }

      setBucketName(credentials.bucket);

      if (credentials.quota_limit) {
        const maxSizeInBytes = credentials.quota_limit;
        setBucketMaxSize(maxSizeInBytes);
        setHasMaxSize(true);
      }

      if (credentials.expiration_date) {
        setExpirationDate(credentials.expiration_date);
      }

      const s3 = await getS3ClientWithCredentials(credentials);
      setClient(s3);

      const result = await s3.send(
        new ListObjectsV2Command({Bucket: credentials.bucket})
      );

      const transformedFiles: FileItem[] = (result.Contents || []).map((file: any, index: number) => ({
        key: index.toString(),
        name: file.Key || 'Unknown',
        date: file.LastModified ? new Date(file.LastModified).toLocaleDateString('ru-RU') : '-',
        owner: 'Service Delivery Part...',
        size: file.Size ? `${(file.Size / (1024 * 1024)).toFixed(2)} MB` : '0 MB',
        sizeInBytes: file.Size || 0,
        type: getFileTypeFromName(file.Key || ''),
        s3Key: file.Key || ''
      }));

      const totalSize = transformedFiles.reduce((sum, file) => sum + file.sizeInBytes, 0);
      setUsedStorage(totalSize);
      setFiles(transformedFiles);
    } catch (error) {
      console.error('Failed to get credentials:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to get credentials for this token';
      setErrorMessage(errorMsg);
      messageApi.error(errorMsg);
    } finally {
      setGettingCredentials(false);
      setLoading(false);
    }
  }, [accessToken, messageApi])

  const handleGetCredentials = async () => {
    setGettingCredentials(true);
    setErrorMessage('');
    try {
      const {share, credentials} = await getOrCreateShareAndCredentials(userId || undefined);

      setCurrentShareId(share.id.toString());

      if (!credentials.bucket) {
        throw new Error('Failed to get storage access');
      }

      setBucketName(credentials.bucket);

      if (credentials.quota_limit) {
        const maxSizeInBytes = credentials.quota_limit * 1024;
        setBucketMaxSize(maxSizeInBytes);
        setHasMaxSize(true);
      }

      if (credentials.expiration_date) {
        setExpirationDate(credentials.expiration_date);
      }

      const s3 = await getS3ClientWithCredentials(credentials);
      setClient(s3);

      await loadFiles(s3, credentials.bucket);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to get or create share and credentials';
      setErrorMessage(errorMsg);
      messageApi.error(errorMsg);
    } finally {
      setGettingCredentials(false);
    }
  };

  useEffect(() => {
    if (isDirectTokenMode && accessToken && !hasInitialized.current) {
      hasInitialized.current = true;
      handleGetCredentialsForToken();
    } else if (!isDirectTokenMode) {
      setLoading(false);
    }
  }, [accessToken, handleGetCredentialsForToken, isDirectTokenMode]);

  const getFileTypeFromName = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'txt':
        return 'txt';
      case 'doc':
      case 'docx':
        return 'word';
      case 'pdf':
        return 'pdf';
      case 'log':
        return 'log';
      default:
        return 'default';
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'txt':
        return <FileTextOutlined/>;
      case 'word':
        return <FileWordOutlined/>;
      case 'pdf':
        return <FilePdfOutlined/>;
      default:
        return <FileOutlined/>;
    }
  };

  const getFileIconClass = (type: string) => {
    switch (type) {
      case 'txt':
        return 'txt';
      case 'word':
        return 'word';
      case 'log':
        return 'log';
      case 'pdf':
        return 'pdf';
      default:
        return 'default';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        handleUpload(file);
      });
    }
  };

  const updateUploadProgress = useCallback((uploadId: string, percentage: number, speed?: number, timeRemaining?: number) => {
    setUploadingFiles(prev => prev.map(f =>
      f.id === uploadId ? {
        ...f,
        progress: percentage,
        speed: speed || f.speed,
        timeRemaining: timeRemaining || f.timeRemaining
      } : f
    ));
  }, []);

  const handleUpload = useCallback(async (file?: File) => {
    const fileToUpload = file || uploadFile;
    if (!fileToUpload || !client || !bucketName) {
      return;
    }

    const uploadId = Date.now().toString() + Math.random().toString(36).substr(2, 9);

    setUploadingFiles(prev => [...prev, {
      id: uploadId,
      name: fileToUpload.name,
      progress: 0,
      status: 'uploading'
    }]);

    setUploading(true);
    try {
      const fileSize = fileToUpload.size;
      const partSize = fileSize < 5 * 1024 * 1024
        ? 5 * 1024 * 1024
        : Math.min(10 * 1024 * 1024, Math.max(5 * 1024 * 1024, Math.floor(fileSize / 20)));

      const upload = new Upload({
        client: client,
        params: {
          Bucket: bucketName,
          Key: fileToUpload.name,
          Body: fileToUpload,
          ContentType: fileToUpload.type || "application/octet-stream",
        },
        partSize: partSize,
        queueSize: 1,
        leavePartsOnError: false,
      });

      let lastUpdate = 0;
      let lastLoaded = 0;
      let lastTime = Date.now();

      upload.on("httpUploadProgress", (progress) => {
        const now = Date.now();
        if (now - lastUpdate < 100) return;

        lastUpdate = now;
        if (progress.loaded && progress.total) {
          const percentage = Math.round((progress.loaded / progress.total) * 100);

          const timeDiff = (now - lastTime) / 1000;
          const loadedDiff = progress.loaded - lastLoaded;
          const speed = loadedDiff / timeDiff;

          const remainingBytes = progress.total - progress.loaded;
          const timeRemaining = speed > 0 ? remainingBytes / speed : 0;

          updateUploadProgress(uploadId, percentage, speed, timeRemaining);

          lastLoaded = progress.loaded;
          lastTime = now;
        }
      });

      await upload.done();

      setUploadingFiles(prev => prev.map(f =>
        f.id === uploadId
          ? {...f, progress: 100, status: 'success'}
          : f
      ));

      const newFile = {
        key: Date.now().toString(),
        name: fileToUpload.name,
        date: new Date().toLocaleDateString('ru-RU'),
        owner: 'Service Delivery Part...',
        size: `${(fileToUpload.size / (1024 * 1024)).toFixed(2)} MB`,
        sizeInBytes: fileToUpload.size,
        type: getFileTypeFromName(fileToUpload.name),
        s3Key: fileToUpload.name
      };

      setFiles(prev => [newFile, ...prev]);
      setUsedStorage(prev => prev + fileToUpload.size);
      setUploadFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      }, 2000);
    } catch (error) {
      setUploadingFiles(prev => prev.map(f =>
        f.id === uploadId
          ? {...f, status: 'error'}
          : f
      ));

      setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      }, 3000);

      messageApi.error('Upload failed!');
    } finally {
      setUploading(false);
    }
  }, [uploadFile, client, bucketName, updateUploadProgress, messageApi]);

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop: useCallback((acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        acceptedFiles.forEach((file) => {
          handleUpload(file);
        });
      }
    }, [handleUpload]),
    multiple: true,
    disabled: !!errorMessage || !bucketName || uploading,
  });

  const handleDownload = async (file: FileItem) => {
    if (!client || !file.s3Key || !bucketName) return;

    setDownloadingFiles(prev => new Map(prev.set(file.key, {
      progress: 0,
      status: 'downloading'
    })));

    try {
      const s3Client = new S3Client({
        region: client.config.signingRegion,
        credentials: client.config.credentials,
        endpoint: process.env.REACT_APP_S3_BASE_URL,
        forcePathStyle: client.config.forcePathStyle || true,
      });

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: file.s3Key,
        ResponseContentDisposition: `attachment; filename="${file.name}"`,
        ResponseContentType: 'application/octet-stream'
      });

      const presignedUrl = await getSignedUrl(s3Client, command, {
        expiresIn: 3600
      });

      const downloadLink = document.createElement('a');
      downloadLink.href = presignedUrl;
      downloadLink.download = file.name;
      downloadLink.style.display = 'none';

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setDownloadingFiles(prev => new Map(prev.set(file.key, {
        progress: 100,
        status: 'success'
      })));

      messageApi.success(`Download started for ${file.name}!`);

      setTimeout(() => {
        setDownloadingFiles(prev => {
          const newMap = new Map(prev);
          newMap.delete(file.key);
          return newMap;
        });
      }, 2000);

    } catch (error) {
      console.error("Download failed:", error);

      setDownloadingFiles(prev => new Map(prev.set(file.key, {
        progress: 0,
        status: 'error'
      })));

      messageApi.error('Download failed');

      setTimeout(() => {
        setDownloadingFiles(prev => {
          const newMap = new Map(prev);
          newMap.delete(file.key);
          return newMap;
        });
      }, 3000);
    }
  };

  const handleDelete = async (file: FileItem) => {
    if (!client || !file.s3Key || !bucketName) return;

    try {
      await client.send(
        new DeleteObjectCommand({Bucket: bucketName, Key: file.s3Key})
      );

      setFiles(prev => prev.filter(f => f.key !== file.key));
      setUsedStorage(prev => prev - file.sizeInBytes);
      messageApi.success('File deleted successfully!');
    } catch (error) {
      messageApi.error('Delete failed!');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: FileItem) => (
        <div className="file-name">
          <span className={`file-icon ${getFileIconClass(record.type)}`}>
            {getFileIcon(record.type)}
          </span>
          {name}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    // {
    //   title: 'Owner',
    //   dataIndex: 'owner',
    //   key: 'owner',
    // },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: any, record: FileItem) => (
        <div className="file-actions">
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="delete" onClick={() => handleDelete(record)}>
                  Delete
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <MoreOutlined/>
          </Dropdown>
          <div className="download-container">
            <Button
              disabled={downloadingFiles.has(record.key)}
              type="text"
              icon={<DownloadOutlined/>}
              onClick={() => handleDownload(record)}
              className="download-button"
              title="Download file"
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="file-browser-container">
      <div className="header-section">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{display: 'none'}}
        />
        <div
          {...getRootProps()}
          className={`full-width-dropzone ${isDragActive ? 'drag-active' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            {uploadingFiles.length > 0 && (
              <div className="uploading-files-list">
                {uploadingFiles.map((file) => (
                  <div key={file.id} className="uploading-file-item">
                    <div className="file-name">
                      {file.name} - {file.progress}%
                      {file.speed && file.timeRemaining && (
                        <span className="upload-stats">
                          {' '}({formatSpeed(file.speed)} - {formatTime(file.timeRemaining)})
                        </span>
                      )}
                    </div>
                    <Progress
                      percent={file.progress}
                      size="small"
                      status={file.status === 'error' ? 'exception' : file.status === 'success' ? 'success' : 'active'}
                      showInfo={false}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="upload-section">
              <Button
                className="upload-button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                loading={uploading}
                icon={<UploadOutlined className="upload-icon"/>}
                disabled={!!errorMessage || !bucketName}
              >
                {isDragActive ? 'Drop files here' : 'Upload files'}
              </Button>
              <div className="drop-zone-text">
                {isDragActive ? 'Release to upload files' : 'or drop files here'}
              </div>
            </div>

            {!isDirectTokenMode && (
              <div className="credentials-section">
                <Input
                  placeholder="Enter User ID (optional, default: 1)"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  style={{marginBottom: '8px'}}
                />
                <Button
                  className="credentials-button"
                  onClick={handleGetCredentials}
                  loading={gettingCredentials}
                  icon={<KeyOutlined/>}
                >
                  {userId ? 'Get/Create Share & Credentials' : 'Create New Share & Get Credentials'}
                </Button>
                {currentShareId && (
                  <div className="current-share-id">
                    Current Share ID: {currentShareId}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="storage-section">
          <div className="storage-title">Total used</div>
          <div className="storage-usage">
            {hasMaxSize
              ? `${formatSize(usedStorage)} of ${formatSize(bucketMaxSize)} used`
              : `${formatSize(usedStorage)}`
            }
          </div>
          {hasMaxSize && (
            <div className="storage-progress">
              <Progress
                percent={Math.round(storagePercentage)}
                showInfo={false}
                strokeWidth={8}
              />
            </div>
          )}
          {expirationDate && (
            <div className="expiration-date">
              Expires: {formatExpirationDate(expirationDate)}
            </div>
          )}
        </div>
      </div>

      <div className="files-section">
        {errorMessage ? (
          <div className="error-message">
            <div className="error-text">{errorMessage}</div>
          </div>
        ) : (
          <>
            <div className="files-title">All files</div>
            <Table
              columns={columns}
              dataSource={files}
              pagination={false}
              className="files-table"
              rowKey="key"
              loading={loading}
            />
          </>
        )}
      </div>
    </div>
  );
}
